import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BrandResult } from "./builder/BrandResult";
import { MarketingResult } from "./builder/MarketingResult";
import { SalesResult } from "./builder/SalesResult";
import { FinanceResult } from "./builder/FinanceResult";
import { CompetitorsResult } from "./builder/CompetitorsResult";
import { WebsiteResult } from "./builder/WebsiteResult";

const MODULES = [
  { id: "brand", label: "Brand", desc: "Logo IA, colors, slogan, mission", Result: BrandResult },
  { id: "marketing", label: "Marketing", desc: "Ads, SEO, emails, press", Result: MarketingResult },
  { id: "sales", label: "Sales", desc: "Cold emails, script, objections", Result: SalesResult },
  { id: "finance", label: "Finance", desc: "CAC, LTV, break-even, forecast", Result: FinanceResult },
  { id: "competitors", label: "Competitors", desc: "Rivals, gaps, moat", Result: CompetitorsResult },
  { id: "website", label: "Website", desc: "AI landing page, hosted live in 60s", Result: WebsiteResult },
];

function generateLabel(tab, isLoading) {
  if (isLoading) {
    if (tab === "brand") return "Designing brand + logo… (~45s)";
    if (tab === "website") return "Building + publishing your site… (~60s)";
    return "Generating…";
  }
  if (tab === "website") return "Generate & publish the website →";
  const label = MODULES.find((m) => m.id === tab)?.label;
  return `Generate ${label} →`;
}

export const BusinessBuilder = ({ trendId, businessName, tagline, pitch, products }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = MODULES.some((m) => m.id === urlParams.get("bbtab"))
    ? urlParams.get("bbtab")
    : "brand";
  const [tab, setTab] = useState(initialTab);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(null);

  // Restore previously generated modules (owner/premium — errors are silent)
  useEffect(() => {
    api.get(`/business/results/${trendId}`)
      .then((r) => {
        setResults((prev) => ({ ...r.data, ...prev }));
        if (urlParams.has("bbscroll")) {
          setTimeout(() => {
            document.querySelector("[data-testid='business-builder']")
              ?.scrollIntoView({ block: "start" });
          }, 600);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendId]);

  const generate = async (moduleId) => {
    setLoading(moduleId);
    try {
      const body = { trend_id: trendId, business_name: businessName, tagline };
      if (moduleId === "website") {
        body.brand = results.brand || null;
        body.pitch = pitch || null;
        body.products = products || null;
      }
      const { data } = await api.post(`/business/${moduleId}`, body);
      setResults((r) => ({ ...r, [moduleId]: moduleId === "website" ? data : data.result }));
      toast.success(`${MODULES.find((m) => m.id === moduleId)?.label} generated`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail === "premium_required"
        ? "Premium required for this module"
        : detail || "Generation failed — try again");
    }
    setLoading(null);
  };

  const active = MODULES.find((m) => m.id === tab);
  const r = results[tab];

  return (
    <div data-testid="business-builder" className="mt-8 border border-black">
      <div className="p-5 border-b border-black bg-[#f4f4f5] flex flex-wrap items-baseline gap-3">
        <span className="overline">Business Builder V2</span>
        <span className="font-mono text-xs text-[#52525b]">
          {businessName} · {MODULES.length} AI modules
        </span>
      </div>

      <div className="flex flex-wrap border-b border-black">
        {MODULES.map((m) => (
          <button
            key={m.id}
            data-testid={`bb-tab-${m.id}`}
            onClick={() => setTab(m.id)}
            className={`px-4 lg:px-6 py-3 overline border-r border-black transition-colors ${
              tab === m.id ? "bg-black text-white" : "bg-white hover:bg-[#f4f4f5]"
            } ${results[m.id] ? "after:content-['✓'] after:ml-1 after:text-[#0a7d33]" : ""}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="p-5 lg:p-8">
        {!r && (
          <div className="text-center py-8">
            <div className="overline text-[#52525b] mb-2">{active?.desc}</div>
            <button
              data-testid={`bb-generate-${tab}`}
              onClick={() => generate(tab)}
              disabled={loading === tab}
              className="hard-shadow border border-black bg-black text-white px-8 py-4 overline disabled:opacity-50"
            >
              {generateLabel(tab, loading === tab)}
            </button>
            {tab === "website" && !results.brand && (
              <div className="mt-3 font-mono text-xs text-[#52525b]">
                Tip: generate the Brand module first — the site will use your logo colors &amp; slogan.
              </div>
            )}
          </div>
        )}

        {r && active?.Result && <active.Result r={r} />}

        {r && (
          <div className="mt-5 text-right">
            <button
              data-testid={`bb-regenerate-${tab}`}
              onClick={() => generate(tab)}
              disabled={loading === tab}
              className="border border-black px-5 py-2 overline hover:bg-black hover:text-white disabled:opacity-50"
            >
              {loading === tab ? "…" : "Regenerate ↻"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessBuilder;
